import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/liveOps/bonuses/slice";
import { ModalBonusSettingsTabs } from "configs/types";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import BonusesSettingsActivationRulesTab from "./BonusesSettingsActivationRulesTab";
import BonusesSettingsRewardsTab from "./BonusesSettingsRewardsTab";
import Tooltip from "../../../../components/Tooltip";

interface Props {
  currency: string;
  tabs: ModalBonusSettingsTabs[];
  activeTab: ModalBonusSettingsTabs;
  setActiveTab: (e: ModalBonusSettingsTabs) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
}
const BonusesSettingsTab: React.FC<Props> = ({
  currency,
  tabs,
  activeTab,
  setActiveTab,
  fieldErrors,
  clearFieldError,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store.bonuses.payload);
  const currentItem = payload?.additionalConfigs.filter(
    (el) => el?.currency === currency,
  )[0];

  const tooltipMap: Record<ModalBonusSettingsTabs, string> = {
    [ModalBonusSettingsTabs.activationRules]: t(
      "triggerConditionForBonusActivation",
    ),
    [ModalBonusSettingsTabs.rewards]: t("creditedToThePlayerAfterFulfilling"),
    [ModalBonusSettingsTabs.betsRange]: t("minAndMaxBet"),
  };

  const handleDataBets = (key: string, value: string) => {
    dispatch(
      // @ts-ignore
      handleUpdateFields({
        ...payload,
        additionalConfigs: [
          // @ts-ignore
          ...payload?.additionalConfigs.filter(
            (el) => el?.currency !== currency,
          ),
          {
            ...payload?.additionalConfigs.filter(
              (el) => el?.currency === currency,
            )[0],
            [key]: value,
          },
        ],
      }),
    );
  };

  const handleTabClick = (e: ModalBonusSettingsTabs) => {
    setActiveTab(e);
  };

  const renderTab = () => {
    if (activeTab === ModalBonusSettingsTabs.activationRules) {
      return (
        <BonusesSettingsActivationRulesTab
          fieldErrors={fieldErrors}
          clearFieldError={clearFieldError}
          currency={currency}
        />
      );
    }
    if (activeTab === ModalBonusSettingsTabs.rewards) {
      return (
        <BonusesSettingsRewardsTab
          fieldErrors={fieldErrors}
          clearFieldError={clearFieldError}
          currency={currency}
        />
      );
    }
    if (activeTab === ModalBonusSettingsTabs.betsRange) {
      return (
        <div className="bonus-bet-limits-wrap">
          <div className="bonus-form-box">
            <div className="bonus-form-box--item">
              <InnerInput
                value={currentItem?.betsRangeMin?.toString() ?? ""}
                placeholder={t("minBet")}
                label={t("minBet")}
                tooltipText={t("minBetTooltip")}
                tooltipPosition="top-left"
                type="number"
                id="bets-min"
                onChange={(e) => {
                  handleDataBets("betsRangeMin", e);
                  clearFieldError("min");
                }}
                hasError={Boolean(fieldErrors?.["min"])}
              />
            </div>
          </div>
          <div className="bonus-form-box">
            <div className="bonus-form-box--item">
              <InnerInput
                value={currentItem?.betsRangeMax?.toString() ?? ""}
                placeholder={t("maxBet")}
                label={t("maxBet")}
                tooltipText={t("maxBetTooltip")}
                tooltipPosition="top-left"
                type="number"
                id="bets-max"
                onChange={(e) => {
                  handleDataBets("betsRangeMax", e);
                  clearFieldError("max");
                }}
                hasError={Boolean(fieldErrors?.["max"])}
              />
            </div>
          </div>
        </div>
      );
    }
    
    return <div className="game-form-wrapper"></div>;
  };

  return (
    <>
      <div className="game-modal-tabs segment-tabs bonus-settings-tab small">
        {tabs.map((el) => {
          return (
            <div
              className={`tab-item ${activeTab === el ? "active" : ""}`}
              key={el}
              onClick={() => handleTabClick(el)}
            >
              {t(el)}
              <Tooltip text={tooltipMap[el]} position="top">
                <span className="info-icon">
                  ℹ
                </span>
              </Tooltip>
            </div>
          );
        })}
      </div>
      <div className="bonus-form-content--settings">{renderTab()}</div>
    </>
  );
};

export default BonusesSettingsTab;
